import { create } from 'zustand';
import { PlayDetailState, PlayDetailStore, SeasonType, PlayPageFocusModule } from '@/types/playDetail';
import { useHistoryStore } from '@/stores/history';
import { PlayDetailService } from './service';
import { handleDetailInfo, createVideoGroups, updateArrayItem, getOptionList } from './utils/utils';

const { fetchPlayUrl, pullAndUpdateSeasonList } = PlayDetailService;
const { OptionList } = PlayPageFocusModule;
const autoEnterPlayerPageTime = 20000;

const createInitialState = (): PlayDetailState => ({
  pureImgOpUrl: '',
  artTitleImg: '',
  mainTitle: '',
  tagLabelList: [],
  description: '',
  updateInfo: '',
  optionList: [],
  recommendList: [],

  curShowSeason: [],
  defSeasonIdx: -1,

  autoPlayVideoInfo: {
    cid: '',
    vid: '',
    videoPlayTime: 0,
    title: '',
    isHistory: false,
  },
  isShowDetailDescription: false,
  isShowSeasonListPage: false,
  isDescriptOverFlow: false,

  isLoading: true,
  isLoaded: false,
  isError: false,

  selectSeasonIdx: 0,
  selectGroupIdx: 0,
  selectPosterIdx: 0,

  selectModule: OptionList,

  timer: null,
  autoEnterPlayerPage: false,
  nextVidData: {},
});

export const usePlayDetailStore = create<PlayDetailStore>((set, get) => {
  const initialState = createInitialState();
  return {
    ...initialState,
    setSeasonIdx: async (idx, autoPlayVideoData) => {
      try {
        const { curShowSeason } = get();
        const { initialRequestParams, seasonType, isLoaded, isLoading } = curShowSeason[idx] || {};
        if (isLoading || isLoaded) return; // 加载中避免重复加载，加载完成不加载
        if (!initialRequestParams || !seasonType) return;
        const {
          nextPageInfo: { has_next_page: hasNextPage },
        } = initialRequestParams;
        if (!hasNextPage) {
          get().updateCurShowSeason(idx, { isLoaded: true, isLoading: false, isError: false });
        } else {
          get().updateCurShowSeason(idx, { isLoading: true, isError: false });
          const seasonVideoListData = await get().requestSeasonVideoList(initialRequestParams);
          const {
            groupList,
            defGroupIdx,
            defPosterIdx,
            autoPlayVideoData: handledAutoPlayVideoData,
            nextVidInfo,
          } = createVideoGroups({
            videoList: seasonVideoListData,
            isRelatedVideo: seasonType === SeasonType.SeasonRelatedvideo,
            autoPlayVideoData,
          });
          // 只有初始化传入的autoPlayVideoData才有handledAutoPlayVideoData
          if (handledAutoPlayVideoData) {
            const optionList = getOptionList(handledAutoPlayVideoData);
            set({ optionList, autoPlayVideoInfo: handledAutoPlayVideoData });
          }
          get().updateCurShowSeason(idx, {
            groupList,
            defGroupIdx,
            defPosterIdx,
            isLoaded: true,
            isLoading: false,
            isError: false,
          });
          get().updateNextVidData(idx, nextVidInfo);
        }
      } catch {
        if (autoPlayVideoData) {
          const optionList = getOptionList(autoPlayVideoData);
          set({ optionList, autoPlayVideoInfo: autoPlayVideoData });
        }
        get().updateCurShowSeason(idx, { isLoaded: false, isLoading: false, isError: true });
      }
    },

    updateCurShowSeason: (idx, curSeason) => {
      const { curShowSeason } = get();
      const newCurShowSeason = updateArrayItem(curShowSeason, idx, () => ({
        ...curShowSeason[idx],
        ...curSeason,
      }));
      set({ curShowSeason: newCurShowSeason });
    },

    updateNextVidData: (idx, nextVidInfo) => {
      const { curShowSeason, nextVidData } = get();
      const { [curShowSeason?.[idx]?.cid]: curNextVidInfo = {} } = nextVidData;
      const nextVidDataRes = {
        ...nextVidData,
        [curShowSeason[idx].cid]: {
          ...(curNextVidInfo || {}),
          ...nextVidInfo,
        },
      };
      set({ nextVidData: nextVidDataRes });
    },

    requestSeasonVideoList: async (requestParams) => {
      const { videoList } = await pullAndUpdateSeasonList(requestParams);
      return videoList;
    },

    init: async (cid, vid, isVip) => {
      const { reset } = get();
      reset();
      const { queryHistoryByCid } = useHistoryStore.getState();
      const historyRecord = queryHistoryByCid(cid);
      try {
        const detailInfo = await fetchPlayUrl(cid, vid, { is_vip: isVip });
        const {
          artTitleImg,
          mainTitle,
          pureImgOpUrl,
          tagLabelList,
          description,
          updateInfo,
          optionList,
          recommendData,
          seasonListData,
          autoPlayVideoData,
          defSeasonIdx,
        } = handleDetailInfo({
          detailInfo,
          historyRecord,
          vidParams: vid,
        });

        set({
          artTitleImg,
          mainTitle,
          pureImgOpUrl,
          tagLabelList,
          description,
          updateInfo,
          optionList,
          recommendList: recommendData,
          curShowSeason: seasonListData,
          autoPlayVideoInfo: autoPlayVideoData,
          defSeasonIdx,
          isLoaded: true,
          isLoading: false,
        });

        get().setSeasonIdx(defSeasonIdx, autoPlayVideoData);
      } catch {
        set({ isError: true, isLoading: false, isLoaded: false });
      }
    },

    reset: () => {
      const initialState = createInitialState();
      set(initialState);
    },

    resetLoadStatus: () => {
      const initialState = createInitialState();
      const { nextVidData } = get();
      set({
        ...initialState,
        nextVidData,
      });
    },

    autoEnterPlayerPageTimer: (refreshTime) => {
      // 如果有定时器并且不需要重新生成，则直接返回
      if (!refreshTime && get().timer) return;
      const { clearAutoEnterPlayerPageTimer } = get();
      clearAutoEnterPlayerPageTimer();
      const curTimer = window.setTimeout(() => {
        get().setKeyVal('autoEnterPlayerPage', true);
        set({
          timer: null,
          autoEnterPlayerPage: true,
        });
      }, autoEnterPlayerPageTime);
      set({ timer: curTimer });
    },

    clearAutoEnterPlayerPageTimer: () => {
      const { timer } = get();
      if (timer) {
        window.clearTimeout(timer);
        set({ timer: null, autoEnterPlayerPage: false });
      }
    },

    getNextVid: (cid, vid) => {
      const { nextVidData } = get();
      return nextVidData[cid]?.[vid]?.nextVid || '';
    },

    getVidType: (cid, vid) => {
      const { nextVidData } = get();
      return nextVidData[cid]?.[vid]?.type;
    },

    keyToVal: (key) => {
      const { [key]: val } = get();
      return val;
    },

    setKeyVal: (key, val) => {
      set({ [key]: val });
    },
  };
});
